import { Button } from '@/components/form/baseInputs/Button/Button';
import { Stars01Icon } from '@/components/icons/Stars01Icon';
import { COLORS } from '@/styles/tokens/colors';

import { useSlideEditorPromptContext } from './SlideEditor.promptContext';
import { PromptPopoverContent } from './SlideEditor.promptPopover';
import {
  PROMPT_POPOVER_HEIGHT_PX,
  PROMPT_POPOVER_WIDTH_PX,
} from './SlideEditor.promptPopoverConstants';
import { SlideEditorToolbarDivider } from './SlideEditor.toolbarDivider';
import { SlideEditorToolbarPopover } from './SlideEditor.toolbarDropdown';

/**
 * AI prompt button for the slide editor toolbar. Opens a popover anchored to
 * the toolbar's left edge for selecting/entering an AI prompt.
 *
 * The outer wrapper is always rendered (even when hidden) so it occupies a
 * stable position in the parent Stack, matching the original layout where an
 * empty Box held the prompt section's place.
 *
 * Must be rendered inside a SlideEditorToolbar context.
 */
export function SlideEditorPromptButton() {
  const promptContext = useSlideEditorPromptContext();

  if (!promptContext) return null;

  return (
    <>
      <SlideEditorToolbarPopover
        dropdownKey="prompt"
        anchorToToolbar
        width={PROMPT_POPOVER_WIDTH_PX}
        height={PROMPT_POPOVER_HEIGHT_PX}
        paperSx={{ p: 2 }}
        renderTrigger={({ isOpen }) => <PromptTriggerButton isOpen={isOpen} />}
      >
        <PromptPopoverContent />
      </SlideEditorToolbarPopover>
      <SlideEditorToolbarDivider />
    </>
  );
}

function PromptTriggerButton({ isOpen }: { isOpen: boolean }) {
  const promptContext = useSlideEditorPromptContext();
  const isGenerating = promptContext?.generationStatus === 'generating';

  let buttonLabel: string;
  if (isGenerating) {
    buttonLabel = 'Generating...';
  } else if (promptContext?.customPrompt) {
    buttonLabel = 'Regenerate';
  } else if (!promptContext?.isTemplate) {
    buttonLabel = 'Generate';
  } else {
    buttonLabel = 'Use prompt';
  }

  return (
    <Button
      variant="transparent"
      size="xs"
      disabled={isGenerating}
      startIcon={Stars01Icon}
      sx={
        isOpen
          ? {
              '&&': { backgroundColor: COLORS.GRAY[50] },
            }
          : undefined
      }
    >
      {buttonLabel}
    </Button>
  );
}
